import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { openExternalUrl } from '@/utils/openUrl';
import { calculateAge } from '@/utils/age';
import { Input } from '@/components/ui/Input/Input';
import { SingleDatePicker } from '@/components/ui';
import { PhoneInput } from '@/components/ui/PhoneInput/PhoneInput';
import { Checkbox } from '@/components/ui/Checkbox/Checkbox';
import { PasswordValidation } from '@/components/ui/PasswordValidation/PasswordValidation';
import { supabase } from '@/lib/supabase';
import { PhoneValidationResult } from '@/components/ui/PhoneInput/PhoneInput';
import { useToast } from '@/hooks/useToast';
import { Toast } from '@/components/ui/Toast/Toast';
import { DuplicateEmailHelpLinks } from '@/components/auth/DuplicateEmailHelpLinks';
import { readEdgeFunctionErrorData } from '@/utils/edgeFunctionError';

interface SignupFormProps {
  loading: boolean;
  setLoading: (loading: boolean) => void;
  onSignupSuccess: () => void;
  /** When true, disables the submit button (e.g. social OAuth redirect in progress). */
  oauthInFlight?: boolean;
}

export const SignupForm = ({
  loading,
  setLoading,
  onSignupSuccess,
  oauthInFlight = false,
}: SignupFormProps) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    phoneCountryCode: 'US',
    dateOfBirth: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [phoneError, setPhoneError] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [duplicateEmail, setDuplicateEmail] = useState(false);
  const { showToast, hideToast, toast } = useToast();

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    
    if (password.length < 6) {
      errors.push('Password must be at least 6 characters long');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Your password must include one number');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Your password must include one uppercase letter');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Your password must include one lowercase letter');
    }
    
    return errors;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    if (field === 'password') {
      const errors = validatePassword(value);
      setPasswordErrors(errors);
    }
    
    if (error) setError(null);
    if (field === 'email') setDuplicateEmail(false);
  };

  const handlePhoneValidation = (validation: PhoneValidationResult) => {
    if (!validation.isValid) {
      setPhoneError(validation.error || 'Invalid phone number');
    } else {
      setPhoneError('');
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setShowValidation(true);
    setError(null);
    setDuplicateEmail(false);

    if (!formData.firstName.trim()) {
      setError('Please enter your first name');
      return;
    }
    
    if (!formData.lastName.trim()) {
      setError('Please enter your last name');
      return;
    }
    
    if (!formData.email.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setError('Please enter a valid email address');
      return;
    }
    
    if (!formData.phone.trim()) {
      setError('Please enter your phone number');
      return;
    }

    if (phoneError) {
      setError(phoneError);
      return;
    }
    
    if (!formData.password.trim()) {
      setError('Please enter a password');
      return;
    }
    
    const passwordValidationErrors = validatePassword(formData.password);
    if (passwordValidationErrors.length > 0) {
      setError('Please fix password requirements');
      return;
    }
    
    if (!formData.confirmPassword.trim()) {
      setError('Please confirm your password');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (!acceptedTerms) {
      setError('You must accept the Terms and Conditions to create an account');
      return;
    }

    try {
      setLoading(true);

      const failSignup = (message: string, isDuplicate = false) => {
        setError(message);
        setDuplicateEmail(isDuplicate);
        setLoading(false);
      };

      const age = formData.dateOfBirth ? calculateAge(formData.dateOfBirth) : null;
      const e164Phone = formData.phone;
      const normalizedEmail = formData.email.trim().toLowerCase();

      const { data, error: signupError } = await supabase.functions.invoke('create-user-account', {
        body: {
          email: normalizedEmail,
          password: formData.password,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          phone: e164Phone,
          dateOfBirth: formData.dateOfBirth || null,
          age: age,
        },
      });

      if (data && data.success === false) {
        if (data.error === 'EMAIL_ALREADY_EXISTS') {
          failSignup(
            'An account with this email address already exists. Please log in instead.',
            true,
          );
          return;
        }
        failSignup(data.error || data.message || 'Failed to create account. Please try again.');
        return;
      }

      if (signupError) {
        const errorData = readEdgeFunctionErrorData(signupError);
        const errorCode = errorData?.error || '';
        const parsedErrorMessage = errorData?.message || '';
        const errorMessage = (errorCode || parsedErrorMessage || signupError.message || '').toLowerCase();
        const isDuplicateEmail = 
          errorCode === 'EMAIL_ALREADY_EXISTS' ||
          errorMessage.includes('email_already_exists') ||
          errorMessage.includes('already registered') ||
          errorMessage.includes('a user with this email address has already been registered');

        if (isDuplicateEmail) {
          failSignup(
            'An account with this email address already exists. Please log in instead.',
            true,
          );
          return;
        }
        
        failSignup(errorCode || parsedErrorMessage || signupError.message || 'Failed to create account. Please try again.');
        return;
      }

      if (!data || !data.success) {
        failSignup('Failed to create account. Please try again.');
        return;
      }

      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }

      showToast('Account created successfully! Please log in to continue.', 'success');
      setTimeout(() => {
        onSignupSuccess();
      }, 2000);
    } catch (err: unknown) {
      setDuplicateEmail(false);
      setError(err instanceof Error && err.message ? err.message : 'An error occurred during signup');
      setLoading(false);
    }
  };

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
      <Flex as="form" onSubmit={handleSubmit} w="full" maxW="400px" direction="column" align="center">
        {duplicateEmail ? (
          <Box w="full" p="3" bg="rgba(255,59,48,0.1)" color="volyx.error" borderRadius="lg" fontSize="sm" mb="4" textAlign="center">
            <Box mb="1">{error}</Box>
            <DuplicateEmailHelpLinks />
          </Box>
        ) : error ? (
          <Box w="full" p="3" bg="rgba(255,59,48,0.1)" color="volyx.error" borderRadius="lg" fontSize="sm" mb="4" textAlign="center">
            {error}
          </Box>
        ) : null}

        <Flex w="full" direction="column" gap="4" mb="6">
          <Flex gap="4" w="full" css={{ '& > *': { flex: 1, marginBottom: 0 } }}>
            <Input
              id="signup-firstName"
              name="firstName"
              type="text"
              placeholder="First Name*"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              autoComplete="given-name"
              required
              error={showValidation && !formData.firstName.trim() ? 'First name is required' : undefined}
            />
            
            <Input
              id="signup-lastName"
              name="lastName"
              type="text"
              placeholder="Last Name*"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              autoComplete="family-name"
              required
              error={showValidation && !formData.lastName.trim() ? 'Last name is required' : undefined}
            />
          </Flex>

          <Input
            id="signup-email"
            name="email"
            type="email"
            placeholder="Email*"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            autoComplete="email"
            required
            error={showValidation && !formData.email.trim() ? 'Email is required' : undefined}
          />

          <PhoneInput
            id="signup-phone"
            name="phone"
            value={formData.phone}
            onChange={(value) => handleInputChange('phone', value)}
            onCountryChange={(countryCode) => handleInputChange('phoneCountryCode', countryCode)}
            onValidationChange={handlePhoneValidation}
            placeholder="Phone Number*"
            error={phoneError || (showValidation && !formData.phone.trim() ? 'Phone number is required' : undefined)}
            defaultCountry={formData.phoneCountryCode}
          />

          <SingleDatePicker
            popover
            showAge
            value={formData.dateOfBirth}
            onChange={(date) => handleInputChange('dateOfBirth', date ?? '')}
            placeholder="Date of Birth"
            maxDate={new Date()}
          />

          <Input
            id="signup-password"
            name="password"
            type="password"
            placeholder="Password*"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            autoComplete="new-password"
            required
            showPasswordToggle
            showPassword={showPassword}
            onPasswordToggle={() => setShowPassword(!showPassword)}
            error={showValidation && !formData.password.trim() ? 'Password is required' : undefined}
          />

          <PasswordValidation password={formData.password} errors={passwordErrors} />

          <Input
            id="signup-confirmPassword"
            name="confirmPassword"
            type="password"
            placeholder="Confirm Password*"
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            autoComplete="new-password"
            required
            showPasswordToggle
            showPassword={showConfirmPassword}
            onPasswordToggle={() => setShowConfirmPassword(!showConfirmPassword)}
            error={
              showValidation && !formData.confirmPassword.trim()
                ? 'Please confirm your password'
                : formData.password !== formData.confirmPassword && formData.confirmPassword
                ? 'Passwords do not match'
                : undefined
            }
          />

          <Checkbox
            checked={acceptedTerms}
            onChange={setAcceptedTerms}
            required
            error={showValidation && !acceptedTerms ? 'You must accept the Terms and Conditions' : undefined}
            label={
              <>
                I accept the{' '}
                <chakra.a
                  href="https://www.volyx.app/terms-conditions.html"
                  color="volyx.primary"
                  fontWeight="semibold"
                  textDecoration="underline"
                  cursor="pointer"
                  _hover={{ opacity: 0.8 }}
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openExternalUrl('https://www.volyx.app/terms-conditions.html');
                  }}
                >
                  Terms and Conditions
                </chakra.a>
              </>
            }
          />
        </Flex>

        <chakra.button
          w="full"
          px="6"
          py="3"
          borderRadius="xl"
          fontWeight="semibold"
          transition="all 0.3s"
          cursor="pointer"
          border="none"
          outline="none"
          bg="volyx.primary"
          color="white"
          boxShadow="md"
          _hover={{ boxShadow: 'lg', transform: 'translateY(-2px) scale(1.02)' }}
          _disabled={{ opacity: 0.7, cursor: 'not-allowed' }}
          disabled={loading || oauthInFlight}
        >
          Create Account
        </chakra.button>

        <Flex align="center" mt="6">
          <Text as="span" color="volyx.textSecondary" fontSize="sm">Already have an account?{' '}</Text>
          <chakra.button
            bg="transparent"
            border="none"
            color="volyx.primary"
            fontSize="sm"
            fontWeight="semibold"
            cursor="pointer"
            p="0"
            transition="opacity 0.2s"
            _hover={{ opacity: 0.8 }}
            _focus={{ outline: 'none', textDecoration: 'underline' }}
            onClick={() => navigate('/login')}
          >
            Log in
          </chakra.button>
        </Flex>
      </Flex>
    </>
  );
};
